import {
  BMButton,
  BMComponentsType,
  BMPrimitives,
  BMRadioButton,
  BMText,
  BMTray,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcSystemToolLanguage16 from "@traveloka/icon-kit-web/react/IcSystemToolLanguage16";
import { Fragment, useEffect, useId, useState } from "react";
import { FlatList, Platform, Pressable, StyleSheet } from "react-native";

import { useScrollLock } from "../../../../components";
import modalManager from "../../../../components/Modal/ModalManager";
import BMView from "../../../../components/View/BMView";
import { Locale } from "../../../../core";
import {
  MobileResources,
  useMobileContentResource,
} from "../../../../core/resource/MobileResourceProvider";
import { appendTestId } from "../../../../utils";
import MenuItem from "../MenuItem";

type Props = {
  locales: Locale[];
  selectedLocale: Locale;
  onSelectLocale: (locale: Locale) => void;
};

const testID = "language";

export default function LanguageMenu({
  locales,
  selectedLocale,
  onSelectLocale,
}: Props) {
  const { BMComponents } = useBMTheme();
  const styles = createStyles(BMComponents);
  const uniqueLocales = getUniqueLocales(locales);
  const { CorporateNavigationMenuList: cr, CorporateLanguage: crl } =
    useMobileContentResource();
  const modalID = useId();

  const [openSelectLang, setOpenSelectLang] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tempSelectedLocale, setTempSelectedLocale] = useState(selectedLocale);

  useScrollLock(!modalManager.empty);

  function handleOpenSelectLang() {
    setOpenSelectLang(true);
    modalManager.add(modalID, setOpenSelectLang);
  }

  function handleCloseSelectLang() {
    modalManager.remove(modalID);
  }

  useEffect(() => {
    return () => modalManager.remove(modalID);
  }, [modalID]);

  return (
    <Fragment>
      <MenuItem
        title={
          <BMView row spacing="md">
            <BMText variant="uiLargeLi">{cr.language}</BMText>
            <BMView style={styles.divider} />
            <BMText variant="uiLargeLi" ink="BMStyleTextInkSecondary">
              {translateLanguage(selectedLocale, crl)}
            </BMText>
          </BMView>
        }
        Icon={IcSystemToolLanguage16}
        onClick={handleOpenSelectLang}
        testID={testID}
      />
      <BMTray
        fitContent
        isVisible={openSelectLang}
        onTrayClose={handleCloseSelectLang}
        footerComponent={() => (
          <BMView row spacing="sm" flex>
            <BMView flex>
              <BMButton
                label={cr.selectLocaleModalCancelCTA}
                size="LARGE"
                role="SECONDARY"
                onPress={handleCloseSelectLang}
                stretchable
                testID={appendTestId(testID, "cancel_button")}
              />
            </BMView>
            <BMView flex>
              <BMButton
                label={cr.selectLocaleModalConfirmCTA}
                size="LARGE"
                role="PRIMARY"
                loading={isLoading}
                onPress={() => {
                  setIsLoading(true);
                  onSelectLocale(tempSelectedLocale);
                }}
                stretchable
                testID={appendTestId(testID, "confirm_button")}
              />
            </BMView>
          </BMView>
        )}
      >
        <BMView spacing="xl">
          <BMText variant="titleNormal" testID={appendTestId(testID, "title")}>
            {cr.selectLocaleModalTitle}
          </BMText>
          <FlatList
            data={uniqueLocales}
            scrollEnabled={false}
            keyExtractor={(item) => item}
            renderItem={({ item }) => {
              const isSelected = item === tempSelectedLocale;
              return (
                <Pressable
                  onPress={() => {
                    setTempSelectedLocale(item);
                  }}
                  testID={appendTestId(testID, item)}
                >
                  <BMView
                    row
                    align="center"
                    spacing="md"
                    justify="between"
                    style={[
                      styles.selectItem,
                      isSelected && styles.activeSelectItem,
                    ]}
                  >
                    <BMText variant="uiLargeLi">
                      {translateLanguage(item, crl)}
                    </BMText>
                    <BMView style={styles.marginRightLG} pointerEvents="none">
                      <BMRadioButton value={isSelected} />
                    </BMView>
                  </BMView>
                </Pressable>
              );
            }}
          />
        </BMView>
      </BMTray>
    </Fragment>
  );
}

function translateLanguage(
  locale: Locale,
  cr: MobileResources["content"]["CorporateLanguage"]
) {
  switch (locale) {
    case Locale.IDID:
      return cr.indonesiaText;
    case Locale.THTH:
      return cr.thailandText;
    case Locale.VNVN:
      return cr.vietnamText;
    case Locale.ENID:
    case Locale.ENTH:
    case Locale.ENVN:
      return cr.englishText;
    default:
      return locale;
  }
}

// Returns a filtered and mapped array of locales, ensuring only one English entry
function getUniqueLocales(locales: Locale[]): Locale[] {
  const seen = new Set<string>();
  const result: Locale[] = [];
  for (const locale of locales) {
    let key = locale;
    if (
      locale === Locale.ENID ||
      locale === Locale.ENTH ||
      locale === Locale.ENVN
    ) {
      // Normalize English locales to a single entry
      key = Locale.ENID;
    }
    if (!seen.has(key)) {
      seen.add(key);
      result.push(locale);
    }
  }
  return result;
}

const createStyles = (BMComponents: BMComponentsType) => {
  return StyleSheet.create({
    container: {
      paddingVertical: BMPrimitives.BMSize.sm,
      paddingHorizontal: BMPrimitives.BMSize.md,
    },
    divider: {
      width: BMPrimitives.BMBorder.width.thick,
      backgroundColor: BMComponents.BMStyleLineBordercolorBold,
    },
    selectItem: {
      paddingVertical: BMPrimitives.BMSize.md,
      paddingHorizontal: BMPrimitives.BMSize.lg,
      borderBottomWidth: BMPrimitives.BMBorder.width.thick,
      borderBottomColor: BMComponents.BMStyleLineBordercolorSubtle,
    },
    activeSelectItem: {
      backgroundColor: BMComponents.BMStyleSurfaceBgcolorHighlight,
    },
    marginRightLG: {
      marginRight: Platform.OS !== "web" ? BMPrimitives.BMSize.lg : 0,
    },
  });
};
